import { clientInstance } from '../utils/http-client'

/**
 * Generic download status response from the unified downloads endpoint.
 * Uses `item_count` instead of source-specific counts (variant_count, document_count).
 */
export interface DownloadStatusResponse {
  request_id: number
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'expired'
  progress_percentage: number
  item_count: number
  download_url?: string
  detail?: string
  expires_at?: string
}

/**
 * Get the status of a download request from the generic status endpoint.
 *
 * @param requestId - The download request ID
 * @returns DownloadStatusResponse with status and download URL if completed
 */
export async function getDownloadStatus(
  requestId: number
): Promise<DownloadStatusResponse> {
  const response = await clientInstance.get<DownloadStatusResponse>(
    `/downloads/${requestId}/status/`
  )
  return response.data
}

/**
 * Request body for starting a V2 deals export.
 */
export interface DealsExportV2Request {
  selected_columns: string[]
  export_type: string
  filter_params: Record<string, unknown>
  sort?: string
}

/**
 * Response from starting a V2 deals export.
 */
export interface DealsExportV2Response {
  request_id: number
  status: string
  progress_percentage: number
  item_count: number
}

/**
 * Start an async V2 deals export via the reports endpoint.
 * Used by DealsTableComponentV2 (deal reports table).
 *
 * @param data - Export configuration (columns, filters, file type)
 * @returns Response with request_id for status polling
 */
export async function startDealsReportExportV2(
  data: DealsExportV2Request
): Promise<DealsExportV2Response> {
  const response = await clientInstance.post<DealsExportV2Response>(
    '/reports/deals/export/v2/',
    data
  )
  return response.data
}

/**
 * Start an async V2 deals export via the contacts endpoint.
 * Used by DealsTableComponent (deals table).
 *
 * @param data - Export configuration (columns, filters, file type)
 * @returns Response with request_id for status polling
 */
export async function startDealsExportV2(
  data: DealsExportV2Request
): Promise<DealsExportV2Response> {
  const response = await clientInstance.post<DealsExportV2Response>(
    '/contacts/deals/export/v2/',
    data
  )
  return response.data
}

// ─── Rebate Export Functions (sync blob pattern via download widget) ──────────

/**
 * Common request body for rebate table exports.
 */
export interface RebateExportRequest {
  selected_columns: string[]
  export_type: string
}

/**
 * Export rebate enrollments for a given offer.
 * Returns a blob for immediate download via the download widget.
 */
export async function exportRebateEnrollments(
  offerId: number,
  data: RebateExportRequest,
  filterParams: Record<string, unknown>,
  signal?: AbortSignal
): Promise<{ blob: Blob; filename: string }> {
  const response = await clientInstance.post(
    `/tradespend/firstbite/offers/${offerId}/enrollments/export/`,
    {
      selected_columns: data.selected_columns,
      export_type: data.export_type,
    },
    { params: filterParams, signal, responseType: 'blob' }
  )
  return {
    blob: response.data,
    filename: `First Bite Export - Rebate Enrollments - ${new Date().toLocaleDateString()}.${data.export_type}`,
  }
}

/**
 * Export attribution codes for a given offer.
 * Returns a blob for immediate download via the download widget.
 */
export async function exportAttributionCodes(
  offerId: number,
  data: RebateExportRequest,
  filterParams: Record<string, unknown>,
  signal?: AbortSignal
): Promise<{ blob: Blob; filename: string }> {
  const response = await clientInstance.post(
    `/tradespend/firstbite/offers/${offerId}/attribution-codes/export/`,
    {
      selected_columns: data.selected_columns,
      export_type: data.export_type,
    },
    { params: filterParams, signal, responseType: 'blob' }
  )
  return {
    blob: response.data,
    filename: `First Bite Export - Attribution Codes - ${new Date().toLocaleDateString()}.${data.export_type}`,
  }
}

/**
 * Export manufacturer invoices.
 * Returns a blob for immediate download via the download widget.
 */
export async function exportManufacturerInvoices(
  data: RebateExportRequest,
  signal?: AbortSignal
): Promise<{ blob: Blob; filename: string }> {
  const response = await clientInstance.post(
    `/tradespend/firstbite/invoices/export/`,
    {
      selected_columns: data.selected_columns,
      export_type: data.export_type,
    },
    { signal, responseType: 'blob' }
  )
  return {
    blob: response.data,
    filename: `First Bite Export - Invoices - ${new Date().toLocaleDateString()}.${data.export_type}`,
  }
}
